import type { TranslateTask } from '../types';
import type { TranslateService } from './translate';
import type { CacheManager } from './cache-manager';

function genId(): string {
  return crypto.randomUUID();
}

function getTextByteLength(text: string): number {
  let len = 0;
  for (let i = 0; i < text.length; i++) {
    len += text.charCodeAt(i) > 0x7f ? 2 : 1;
  }
  return len;
}

function groupBy<T>(items: T[], keyFn: (item: T) => string): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const key = keyFn(item);
    const group = map.get(key);
    if (group) {
      group.push(item);
    } else {
      map.set(key, [item]);
    }
  }
  return map;
}

export interface TranslateQueueOptions {
  chunkSize: number;
  maxConcurrency?: number;
  translateService: TranslateService;
  cacheManager: CacheManager;
}

export class TranslateQueue {
  private pending: TranslateTask[] = [];
  private scheduled = false;
  private chunkSize: number;
  private maxConcurrency: number;
  private activeRequests = 0;
  private waitQueue: Array<() => void> = [];
  private translateService: TranslateService;
  private cacheManager: CacheManager;
  private destroyed = false;

  constructor(options: TranslateQueueOptions) {
    this.chunkSize = options.chunkSize;
    this.maxConcurrency = options.maxConcurrency ?? 6;
    this.translateService = options.translateService;
    this.cacheManager = options.cacheManager;
  }

  submit(
    text: string,
    srcLang: string,
    tgtLang: string,
    format: 'text' | 'html' = 'text',
  ): Promise<string> {
    if (this.destroyed) {
      return Promise.reject(new Error('TranslateQueue has been destroyed'));
    }

    return new Promise((resolve, reject) => {
      this.pending.push({
        id: genId(),
        text,
        srcLang,
        tgtLang,
        format,
        resolve,
        reject,
      });

      if (!this.scheduled) {
        this.scheduled = true;
        queueMicrotask(() => this.flush());
      }
    });
  }

  destroy(): void {
    this.destroyed = true;

    const pendingTasks = this.pending;
    this.pending = [];
    this.scheduled = false;
    const err = new Error('TranslateQueue has been destroyed');
    for (const task of pendingTasks) {
      task.reject(err);
    }

    this.waitQueue = [];
  }

  private flush(): void {
    const tasks = this.pending;
    this.pending = [];
    this.scheduled = false;

    if (this.destroyed) {
      const err = new Error('TranslateQueue has been destroyed');
      for (const task of tasks) {
        task.reject(err);
      }
      return;
    }

    if (tasks.length === 0) return;

    const groups = groupBy(tasks, t => `${t.srcLang}_${t.tgtLang}_${t.format}`);

    for (const [, group] of groups) {
      this.processGroup(group);
    }
  }

  private processGroup(tasks: TranslateTask[]): void {
    const chunks: TranslateTask[][] = [];
    let current: TranslateTask[] = [];
    let currentSize = 0;

    for (const task of tasks) {
      const textSize = getTextByteLength(task.text);

      if (currentSize + textSize > this.chunkSize && current.length > 0) {
        chunks.push(current);
        current = [task];
        currentSize = textSize;
      } else {
        current.push(task);
        currentSize += textSize;
      }
    }
    if (current.length > 0) chunks.push(current);

    for (const chunk of chunks) {
      this.translateChunk(chunk);
    }
  }

  private async translateChunk(chunk: TranslateTask[]): Promise<void> {
    if (this.destroyed) {
      const err = new Error('TranslateQueue has been destroyed');
      for (const task of chunk) {
        task.reject(err);
      }
      return;
    }

    await this.acquireConcurrency();

    try {
      if (this.destroyed) {
        const err = new Error('TranslateQueue has been destroyed');
        for (const task of chunk) {
          task.reject(err);
        }
        return;
      }
      const texts = chunk.map(t => t.text);
      const { srcLang, tgtLang, format } = chunk[0];

      const { hits, misses } = this.cacheManager.lookup(texts, srcLang, tgtLang);

      for (const [index, translation] of hits) {
        chunk[index].resolve(translation);
      }

      if (misses.length > 0) {
        try {
          const missTexts = misses.map(m => m.text);
          const translations = await this.translateService.translate(
            missTexts, srcLang, tgtLang, format,
          );

          const entries = misses.map((m, i) => ({
            text: m.text,
            translation: translations[i],
          }));
          this.cacheManager.store(entries, srcLang, tgtLang);

          misses.forEach((m, i) => chunk[m.index].resolve(translations[i]));
        } catch (error) {
          const err = error instanceof Error ? error : new Error(String(error));
          misses.forEach(m => chunk[m.index].reject(err));
        }
      }
    } finally {
      this.releaseConcurrency();
    }
  }

  private acquireConcurrency(): Promise<void> {
    if (this.activeRequests < this.maxConcurrency) {
      this.activeRequests++;
      return Promise.resolve();
    }
    return new Promise(resolve => {
      this.waitQueue.push(() => {
        this.activeRequests++;
        resolve();
      });
    });
  }

  private releaseConcurrency(): void {
    this.activeRequests--;
    const next = this.waitQueue.shift();
    if (next) next();
  }
}
